import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/Login.css";
import logo from "../assets/login-brand-clean.png";

console.log("Login rendered");

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const navigate = useNavigate();

  const API_BASE_URL = process.env.REACT_APP_API_URL || "https://interventi.synology.me:5050";

  // === BETALUMINA_WEBAUTHN_FASTLOGIN_V2 - 2026-08-19 ===
  const WEBAUTHN_FAST_CREDENTIAL_KEY =
    "betalumina:webauthn:credential-id:v2";

  const isWindowsDevice =
    typeof navigator !== "undefined" &&
    /Windows|Win32|Win64/i.test(
      `${navigator.userAgent || ""} ${navigator.platform || ""}`
    );

  const [rememberedCredentialId, setRememberedCredentialId] = useState(() => {
    try {
      return window.localStorage.getItem(WEBAUTHN_FAST_CREDENTIAL_KEY) || "";
    } catch (_) {
      return "";
    }
  });

  const rememberWebAuthnCredential = (credentialId) => {
    const value = String(credentialId || "").trim();

    if (!/^[A-Za-z0-9_-]{8,2048}$/.test(value)) {
      return;
    }

    try {
      window.localStorage.setItem(WEBAUTHN_FAST_CREDENTIAL_KEY, value);
      setRememberedCredentialId(value);
    } catch (_) {
      // Login ancora utilizzabile anche senza storage locale.
    }
  };
  // === END BETALUMINA_WEBAUTHN_FASTLOGIN_V2 ===


  // === BETALUMINA WEBAUTHN LOGIN V1 NATIVE V7 USERID UTF8 - 2026-08-19 ===
  const webauthnBase64UrlToBuffer = (value) => {
    const normalized = String(value || "")
      .replace(/-/g, "+")
      .replace(/_/g, "/");

    const padded =
      normalized +
      "=".repeat((4 - (normalized.length % 4)) % 4);

    const binary = window.atob(padded);
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    return bytes.buffer;
  };

  // SimpleWebAuthn server v9 usa userID come stringa UTF-8.
  // Challenge e credential ID restano invece base64url.
  const webauthnUtf8ToBuffer = (value) =>
    new TextEncoder().encode(String(value ?? "")).buffer;

  const webauthnBufferToBase64Url = (value) => {
    if (value === null || value === undefined) return null;

    const bytes = new Uint8Array(value);
    let binary = "";

    for (let index = 0; index < bytes.length; index += 1) {
      binary += String.fromCharCode(bytes[index]);
    }

    return window
      .btoa(binary)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/g, "");
  };

  const browserSupportsWebAuthn = () =>
    typeof window !== "undefined" &&
    window.isSecureContext === true &&
    typeof window.PublicKeyCredential !== "undefined" &&
    typeof navigator?.credentials?.create === "function" &&
    typeof navigator?.credentials?.get === "function";

  const webauthnCreationOptions = (options) => ({
    ...options,
    challenge: webauthnBase64UrlToBuffer(options.challenge),
    user: {
      ...options.user,
      id: webauthnUtf8ToBuffer(options.user.id),
    },
    excludeCredentials: Array.isArray(options.excludeCredentials)
      ? options.excludeCredentials.map((credential) => ({
          ...credential,
          id: webauthnBase64UrlToBuffer(credential.id),
        }))
      : [],
  });

  const webauthnRequestOptions = (options) => ({
    ...options,
    challenge: webauthnBase64UrlToBuffer(options.challenge),
    allowCredentials: Array.isArray(options.allowCredentials)
      ? options.allowCredentials.map((credential) => ({
          ...credential,
          id: webauthnBase64UrlToBuffer(credential.id),
        }))
      : [],
  });

  const startRegistration = async (options) => {
    const publicKey = webauthnCreationOptions(options);
    const credential = await navigator.credentials.create({ publicKey });

    if (!credential) {
      throw new Error("Nessuna credenziale WebAuthn restituita");
    }

    const response = credential.response;

    return {
      id: credential.id,
      rawId: webauthnBufferToBase64Url(credential.rawId),
      type: credential.type,
      authenticatorAttachment:
        credential.authenticatorAttachment || undefined,
      clientExtensionResults:
        typeof credential.getClientExtensionResults === "function"
          ? credential.getClientExtensionResults()
          : {},
      response: {
        clientDataJSON:
          webauthnBufferToBase64Url(response.clientDataJSON),
        attestationObject:
          webauthnBufferToBase64Url(response.attestationObject),
        transports:
          typeof response.getTransports === "function"
            ? response.getTransports()
            : [],
      },
    };
  };

  const startAuthentication = async (options) => {
    const publicKey = webauthnRequestOptions(options);
    const credential = await navigator.credentials.get({ publicKey });

    if (!credential) {
      throw new Error("Nessuna credenziale WebAuthn restituita");
    }

    const response = credential.response;

    return {
      id: credential.id,
      rawId: webauthnBufferToBase64Url(credential.rawId),
      type: credential.type,
      authenticatorAttachment:
        credential.authenticatorAttachment || undefined,
      clientExtensionResults:
        typeof credential.getClientExtensionResults === "function"
          ? credential.getClientExtensionResults()
          : {},
      response: {
        clientDataJSON:
          webauthnBufferToBase64Url(response.clientDataJSON),
        authenticatorData:
          webauthnBufferToBase64Url(response.authenticatorData),
        signature:
          webauthnBufferToBase64Url(response.signature),
        userHandle:
          response.userHandle === null
            ? null
            : webauthnBufferToBase64Url(response.userHandle),
      },
    };
  };

  const [webauthnBusy, setWebauthnBusy] = useState(false);

  const webauthnSupported =
    typeof window !== "undefined" &&
    browserSupportsWebAuthn();

  const completeLogin = (data, successMessage) => {
    if (!data?.token || !data?.ruolo) {
      throw new Error("Risposta di autenticazione incompleta");
    }

    localStorage.setItem("token", data.token);
    localStorage.setItem("ruolo", data.ruolo);
    navigate("/dashboard");
    toast.success(successMessage);
  };

  const webauthnErrorMessage = (error, fallback) => {
    const name = String(error?.name || error?.cause?.name || "");

    if (name === "NotAllowedError" || name === "AbortError") {
      return "Operazione biometrica annullata.";
    }

    if (name === "InvalidStateError") {
      return "L'impronta/passkey risulta già registrata su questo dispositivo.";
    }

    return (
      error?.response?.data?.message ||
      error?.message ||
      fallback
    );
  };

  const handlePasskeyLogin = async () => {
    if (!webauthnSupported) {
      toast.error("Questo dispositivo non supporta il login biometrico.");
      return;
    }

    setWebauthnBusy(true);

    try {
      const optionsResponse = await axios.post(
        `${API_BASE_URL}/api/webauthn/auth/options`,
        {},
        { withCredentials: true }
      );

      const authenticationOptions = rememberedCredentialId
        ? {
            ...optionsResponse.data.options,
            allowCredentials: [
              {
                id: rememberedCredentialId,
                type: "public-key",
                transports: ["internal"],
              },
            ],
          }
        : optionsResponse.data.options;

      const credential = await startAuthentication(
        authenticationOptions
      );

      const verifyResponse = await axios.post(
        `${API_BASE_URL}/api/webauthn/auth/verify`,
        {
          challengeId: optionsResponse.data.challengeId,
          credential,
        },
        { withCredentials: true }
      );

      rememberWebAuthnCredential(credential.id);

      completeLogin(
        verifyResponse.data,
        "Accesso con impronta effettuato!"
      );
    } catch (error) {
      toast.error(
        webauthnErrorMessage(
          error,
          "Impossibile accedere con impronta."
        )
      );
    } finally {
      setWebauthnBusy(false);
    }
  };

  const handleEnablePasskey = async () => {
    if (!webauthnSupported) {
      toast.error("Questo dispositivo non supporta WebAuthn.");
      return;
    }

    if (!username.trim() || !password.trim()) {
      toast.error(
        "Per la prima attivazione inserisci username e password."
      );
      return;
    }

    if (
      isWindowsDevice &&
      typeof window.PublicKeyCredential
        ?.isUserVerifyingPlatformAuthenticatorAvailable === "function"
    ) {
      const platformAvailable =
        await window.PublicKeyCredential
          .isUserVerifyingPlatformAuthenticatorAvailable();

      if (!platformAvailable) {
        toast.error(
          "Windows Hello non risulta disponibile su questo PC."
        );
        return;
      }
    }

    setWebauthnBusy(true);

    try {
      const loginResponse = await axios.post(
        `${API_BASE_URL}/login`,
        { username, password },
        { withCredentials: true }
      );

      const token = loginResponse.data?.token;

      if (!token) {
        throw new Error("Login preliminare non riuscito");
      }

      const optionsResponse = await axios.post(
        `${API_BASE_URL}/api/webauthn/register/options`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      const credential = await startRegistration(
        optionsResponse.data.options
      );

      const verifyResponse = await axios.post(
        `${API_BASE_URL}/api/webauthn/register/verify`,
        {
          challengeId: optionsResponse.data.challengeId,
          credential,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (!verifyResponse.data?.verified) {
        throw new Error("Registrazione biometrica non verificata");
      }

      rememberWebAuthnCredential(credential.id);

      completeLogin(
        loginResponse.data,
        isWindowsDevice
          ? "Windows Hello attivato su questo PC!"
          : "Impronta/passkey attivata su questo dispositivo!"
      );
    } catch (error) {
      toast.error(
        webauthnErrorMessage(
          error,
          "Impossibile attivare l'accesso biometrico."
        )
      );
    } finally {
      setWebauthnBusy(false);
    }
  };
  // === END BETALUMINA WEBAUTHN LOGIN V1 NATIVE V6 ===


  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      toast.error("Inserisci username e password.");
      return;
    }
  
    const requestData = { username, password };
      
    try {
      const response = await axios.post(`${API_BASE_URL}/login`, requestData, {
        withCredentials: true,
      });
            localStorage.setItem("token", response.data.token);
      localStorage.setItem("ruolo", response.data.ruolo);
      navigate("/dashboard");
      toast.success("Login effettuato con successo!");
    } catch (error) {
      console.error("❌ Login non riuscito");
      const errorMessage = error.response?.data?.message || "Errore nel login.";
      toast.error(`errorMessage}`);
    }
  };
  return (
    <div className="login-container">
      <img src={logo} alt="Logo Betalumina" className="login-logo" />
      <h2 className="login-title">Accedi</h2>
      <form onSubmit={handleLogin}>
        <input
          type="text"
          placeholder="Username"
          className="login-input"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <input
          type="password"
          placeholder="Password"
          className="login-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <button type="submit" className="login-button">
          Login
        </button>

        {/* BETALUMINA WEBAUTHN LOGIN V1 */}
        {webauthnSupported && (
          <>
            <div className="login-biometric-divider">
              <span>oppure</span>
            </div>

            <button
              type="button"
              className="passkey-login-button"
              onClick={handlePasskeyLogin}
              disabled={webauthnBusy}
            >
              {webauthnBusy
                ? "Verifica in corso..."
                : rememberedCredentialId
                ? isWindowsDevice
                  ? "Accedi con Windows Hello"
                  : "Accedi con impronta"
                : "Accedi con passkey"}
            </button>

            <button
              type="button"
              className="passkey-enroll-button"
              onClick={handleEnablePasskey}
              disabled={webauthnBusy}
            >
              {isWindowsDevice
                ? "Attiva Windows Hello su questo PC"
                : "Attiva impronta su questo telefono"}
            </button>

            <small className="login-biometric-note">
              {rememberedCredentialId
                ? "Accesso rapido attivo su questo dispositivo."
                : "La prima attivazione richiede username e password."}
            </small>
          </>
        )}

      </form>
      <button onClick={() => navigate("/forgot-password")} className="forgot-password-button">
        Recupera Password
      </button>
    </div>
  );
};

export default Login;